import React, { useEffect, useState } from "react";
import { Card, CardContent, Typography, Box, CircularProgress, Button } from "@mui/material";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DescriptionIcon from "@mui/icons-material/Description";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import ImageIcon from "@mui/icons-material/Image";
import AudiotrackIcon from "@mui/icons-material/Audiotrack";
import VideoLibraryIcon from "@mui/icons-material/VideoLibrary";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

const FilePreviewer = ({ fileUrl, fileName }) => {
  const [loading, setLoading] = useState(true);
  const [fileType, setFileType] = useState("");

  useEffect(() => {
    if (fileUrl) {
      const ext = fileUrl.split(".").pop().toLowerCase();
      setFileType(ext);
      setLoading(false);
    }
  }, [fileUrl]);

  if (!fileUrl) {
    return (
      <Typography variant="body2" color="text.secondary">
        No file selected
      </Typography>
    );
  }

  const renderPreview = () => {
    switch (true) {
      case fileType === "pdf":
        return (
          <iframe
            src={fileUrl}
            width="100%"
            height="800px"
            title="PDF Preview"
            style={{ border: "none", borderRadius: 8 }}
          />
        );

      case ["jpg", "jpeg", "png", "gif", "webp"].includes(fileType):
        return (
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <img
              src={fileUrl}
              alt={fileName || "preview"}
              style={{
                maxWidth: "100%",
                maxHeight: "400px",
                borderRadius: 8,
                objectFit: "contain",
              }}
              onLoad={() => setLoading(false)}
            />
          </Box>
        );

      case ["mp4", "webm", "ogg"].includes(fileType):
        return (
          <video
            controls
            width="100%"
            height="400px"
            style={{ borderRadius: 8 }}
            onLoadedData={() => setLoading(false)}
          >
            <source src={fileUrl} type={`video/${fileType}`} />
            Your browser does not support the video tag.
          </video>
        );

      case ["mp3", "wav", "ogg"].includes(fileType):
        return (
          <audio controls style={{ width: "100%" }}>
            <source src={fileUrl} type={`audio/${fileType}`} />
            Your browser does not support the audio element.
          </audio>
        );

      case ["txt", "csv"].includes(fileType):
        return (
          <iframe
            src={fileUrl}
            width="100%"
            height="800px"
            title="Text File Preview"
            style={{ border: "none", borderRadius: 8 }}
          />
        );

      // For Word, Excel, PowerPoint files
      case ["doc", "docx", "xls", "xlsx", "ppt", "pptx"].includes(fileType):
        return (
          <iframe
            src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(fileUrl)}`}
            width="100%"
            height="800px"
            title="Office File Preview"
            style={{ border: "none", borderRadius: 8 }}
          />
        );

      default:
        return (
          <Box sx={{ textAlign: "center", py: 3 }}>
            <InsertDriveFileIcon sx={{ fontSize: 60, color: "text.secondary" }} />
            <Typography variant="body2" sx={{ mt: 1 }}>
              No preview available for this file type.
            </Typography>
            <Button
              variant="outlined"
              color="primary"
              size="small"
              href={fileUrl}
              target="_blank"
              endIcon={<OpenInNewIcon />}
              sx={{ mt: 1 }}
            >
              Open File
            </Button>
          </Box>
        );
    }
  };

  return (
    <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {fileName || "File Preview"}
        </Typography>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          renderPreview()
        )}
      </CardContent>
    </Card>
  );
};

export default FilePreviewer;
